import { Component, OnInit, AfterViewChecked, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CustomReportService, ReportSchema, ReportSchemaGroup, ColumnDef, ReportConfig, ReportQueryPayload, ColumnFilter, ReportFilters } from '../../../../services/customers/custom-report.service';

declare const feather: any;

interface SelectableField extends ReportSchemaGroup {
  selected: boolean;
  locked?: boolean;  // true = always selected, cannot be toggled by user
}

/** Payload de consulta sin paginación: es exactamente lo que se exporta a Excel. */
type ReportBasePayload = Omit<ReportQueryPayload, 'page' | 'limit'>;

/** Claves de filtro legacy aceptadas por el backend (se preservan al cargar vistas). */
const LEGACY_FILTER_KEYS = ['agreement_status', 'sales_person_id', 'city', 'state', 'business_type', 'created_from', 'created_to'];

const PERIOD_AVG_KEY = 'collections.avg_gallons_per_period_month';

/** Día de calendario elegido por el usuario (partes locales) → 'YYYY-MM-DD'. */
const toCalendarDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fromCalendarDay = (ymd: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

interface FieldGroup {
  entity: 'clients' | 'projects' | 'collections' | 'reconciliations';
  label: string;
  icon: string;
  expanded: boolean;
  fields: SelectableField[];
}

@Component({
  selector: 'app-custom-report',
  templateUrl: './custom-report.component.html',
  styleUrls: ['./custom-report.component.scss'],
  standalone: false
})
export class CustomReportComponent implements OnInit, AfterViewChecked, OnDestroy {

  // ── Estado del panel de columnas ──────────────────────────────────────────
  fieldGroups: FieldGroup[] = [];
  panelOpen = true;

  // ── Estado del grid ───────────────────────────────────────────────────────
  gridData: any[] = [];
  gridColumns: ColumnDef[] = [];
  loading = false;
  paging = false;  // overlay sutil durante cambio de página (no destruye el grid)
  hasResults = false;

  // ── Paginación ────────────────────────────────────────────────────────────
  currentPage = 1;
  pageSize = 50;
  totalRecords = 0;
  get totalPages(): number { return Math.ceil(this.totalRecords / this.pageSize); }

  // ── Sort server-side ──────────────────────────────────────────────────────
  currentSort: { field: string; direction: 'asc' | 'desc' } | null = null;

  // ── Filtros ───────────────────────────────────────────────────────────────
  /** Filtros legacy de vistas guardadas (se preservan tal cual; el backend los valida). */
  filters: ReportFilters = {};
  columnFilters: ColumnFilter[] = [];

  // Tipo y actividad del cliente (mismas opciones verificadas que la pantalla de clientes)
  statusFilterOptions: any[] = [
    { field: '', value: '', label: 'All' },
    { field: 'status', value: 'Client', label: 'Clients' },
    { field: 'status', value: 'Prospect', label: 'Prospects' },
    { field: 'active', value: '1', label: 'Actives' },
    { field: 'active', value: 'x', label: 'Inactives' }
  ];
  selectedStatusOptions: any[] = [];   // vacío = All

  // Rango de fechas de recolección (solo afecta a Collection Data)
  collectionRange: { from: string; to: string } | null = null;
  dateFilterValue: { type: string; startDate: Date | null; endDate: Date | null } | null = null;

  /** Payload (sin paginación) del último reporte mostrado: evita exportar algo distinto a lo visible. */
  private lastAppliedKey: string | null = null;
  private activeQuery: Subscription | null = null;
  private gridApi: any = null;

  // ── Propiedades AG Grid ───────────────────────────────────────────────────
  columnDefs: any[] = [];
  gridOptions: any = {};

  // ── Vistas guardadas ──────────────────────────────────────────────────────
  savedConfigs: ReportConfig[] = [];
  showSavedPanel = false;
  saveModalOpen = false;
  saveConfigName = '';
  saveConfigShared = false;

  // ── Mensajes feedback ─────────────────────────────────────────────────────
  feedbackMsg = '';
  feedbackType: 'success' | 'error' | 'info' = 'info';

  constructor(private customReportService: CustomReportService) {}

  ngOnInit(): void {
    this.loadSchema();
    this.loadSavedConfigs();
    this.initAgGrid();
  }

  ngOnDestroy(): void {
    this.activeQuery?.unsubscribe();
  }

  ngAfterViewChecked(): void {
    if (typeof feather !== 'undefined') {
      feather.replace();
    }
  }

  initAgGrid(): void {
    this.gridOptions = {
      domLayout: 'normal',
      defaultColDef: {
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          debounceMs: 600
        },
        resizable: true
      },
      onSortChanged: (event: any) => {
        const columnState = event.api.getColumnState();
        const sortedCol = columnState.find((c: any) => c.sort != null);
        // Resolver binding → clave de catálogo con el catálogo real (antes replace(/_/g, '.')
        // rompía claves con guion bajo como collections.avg_gallons_per_month)
        const nextSort = sortedCol
          ? { field: this.catalogKeyFromBinding(sortedCol.colId), direction: sortedCol.sort }
          : null;
        // Evita consultas repetidas cuando el grid re-aplica el mismo orden (p. ej. al restaurar)
        if (JSON.stringify(nextSort) === JSON.stringify(this.currentSort)) return;
        this.currentSort = nextSort;
        this.runReport(1);
      },
      onFilterChanged: (event: any) => {
        const filterModel = event.api.getFilterModel();
        this.columnFilters = this.mapAgGridFiltersToColumnFilters(filterModel);
        event.api.refreshHeader(); // Re-evalúa clases de header (incl. .ag-header-cell-filtered)
        this.runReport(1);
      }
    };
  }

  mapAgGridFiltersToColumnFilters(filterModel: any): ColumnFilter[] {
    const extracted: ColumnFilter[] = [];
    if (!filterModel) return extracted;

    const opMap: Record<string, ColumnFilter['operator']> = {
      'contains': 'contains',
      'equals': 'eq',
      'startsWith': 'startsWith',
      'endsWith': 'endsWith'
    };

    Object.keys(filterModel).forEach(colId => {
      const condition = filterModel[colId];
      if (!condition) return;

      if (condition.operator && (condition.condition1 || condition.condition2)) {
        if (condition.condition1 && condition.condition1.filter !== undefined && condition.condition1.filter !== null && condition.condition1.filter !== '') {
          const op = opMap[condition.condition1.type] || 'contains';
          extracted.push({
            column: colId,
            value: condition.condition1.filter,
            operator: op
          });
        }
        if (condition.condition2 && condition.condition2.filter !== undefined && condition.condition2.filter !== null && condition.condition2.filter !== '') {
          const op = opMap[condition.condition2.type] || 'contains';
          extracted.push({
            column: colId,
            value: condition.condition2.filter,
            operator: op
          });
        }
      } else {
        if (condition.filter !== undefined && condition.filter !== null && condition.filter !== '') {
          const op = opMap[condition.type] || 'contains';
          extracted.push({
            column: colId,
            value: condition.filter,
            operator: op
          });
        }
      }
    });

    return extracted;
  }

  onGridReady(params: any): void {
    this.gridApi = params?.api ?? null;
  }

  private catalogKeyFromBinding(binding: string): string {
    const field = this.fieldGroups
      .reduce((acc: SelectableField[], g) => acc.concat(g.fields), [])
      .find(f => f.key.replace(/\./g, '_') === binding);
    return field ? field.key : binding; // el backend resuelve/valida el binding si no se encuentra
  }

  private static readonly COLUMN_TOOLTIPS: Record<string, string> = {
    collections_avg_gallons_per_month:
      'Total gallons ÷ months with at least one collection (within the collection period when one is set; otherwise all history). Rounded to the nearest .50.',
    collections_avg_gallons_per_period_month:
      'Total gallons in the collection period ÷ every calendar month the period touches, including months without collections. Requires a collection date range. Rounded to the nearest .50.',
    collections_avg_collections_per_month:
      'Collections ÷ every month of the collection period (months with collections when no period is set). Rounded to the nearest whole number.'
  };

  updateColumnDefs(columns: ColumnDef[]): void {
    const filterableByBinding = new Map<string, boolean>();
    this.fieldGroups.forEach(g => g.fields.forEach(f =>
      filterableByBinding.set(f.key.replace(/\./g, '_'), f.filterable !== false)));
    const sortBinding = this.currentSort ? this.currentSort.field.replace(/\./g, '_') : null;

    this.columnDefs = columns.map(col => {
      const isAvgGallons = col.key === 'collections_avg_gallons'
        || col.key === 'collections_avg_gallons_per_month'
        || col.key === 'collections_avg_gallons_per_period_month';
      const isAvgVisits = col.key === 'collections_avg_collections_per_month';

      let valueFormatter: any = undefined;
      if (isAvgGallons) {
        valueFormatter = (params: any) => params.value != null ? Number(params.value).toFixed(1) : '';
      } else if (isAvgVisits) {
        valueFormatter = (params: any) => params.value != null ? Math.round(Number(params.value)).toString() : '';
      }

      const tooltipText = CustomReportComponent.COLUMN_TOOLTIPS[col.key];
      const hasTooltip = !!tooltipText;
      // Numéricos y fechas: el filtro de texto ($regex) nunca coincide en el backend
      const filterable = filterableByBinding.get(col.key) !== false;

      return {
        field: col.key,
        headerName: col.label,
        minWidth: 150,
        flex: 1,
        resizable: true,
        sortable: true,
        filter: filterable ? 'agTextColumnFilter' : false,
        sort: sortBinding === col.key ? this.currentSort!.direction : null,
        valueFormatter,
        ...(hasTooltip ? { headerTooltip: tooltipText } : {})
      };
    });
  }

  // ── Cargar catálogo ───────────────────────────────────────────────────────
  loadSchema(): void {
    this.customReportService.getSchema().subscribe({
      next: res => {
        if (!res.ok) return;
        this.fieldGroups = [
          {
            entity: 'clients',
            label: 'Basic Information',
            icon: 'user',
            expanded: true,
            fields: res.schema.clients.map(f => ({
              ...f,
              selected: f.key === 'client.name',
              locked:   f.key === 'client.name'
            }))
          },
          {
            entity: 'projects',
            label: 'Contract Information',
            icon: 'file-text',
            expanded: false,
            fields: res.schema.projects.map(f => ({ ...f, selected: false }))
          },
          {
            entity: 'collections',
            label: 'Collection Data',
            icon: 'droplet',
            expanded: false,
            fields: res.schema.collections.map(f => ({ ...f, selected: false }))
          },
          {
            entity: 'reconciliations',
            label: 'Reconciliation Data',
            icon: 'dollar-sign',
            expanded: false,
            fields: res.schema.reconciliations.map(f => ({ ...f, selected: false }))
          }
        ];
      },
      error: () => this.showFeedback('Error loading field catalog. Please try again.', 'error')
    });
  }

  // ── Selección de campos ───────────────────────────────────────────────────
  get selectedFields(): SelectableField[] {
    return this.fieldGroups.reduce((acc: SelectableField[], g) => acc.concat(g.fields.filter(f => f.selected)), []);
  }

  get selectedCount(): number {
    return this.selectedFields.length;
  }

  toggleGroup(group: FieldGroup): void {
    const willOpen = !group.expanded;
    // Comportamiento acordeón exclusivo: abrir uno cierra los demás
    this.fieldGroups.forEach(g => {
      g.expanded = (g === group) ? willOpen : false;
    });
  }

  toggleField(field: SelectableField): void {
    if (field.locked) return;  // campos bloqueados no se pueden desmarcar
    field.selected = !field.selected;
  }

  /** Cualquier campo de Collection Data seleccionado activa el rango y la exigencia de recolecciones. */
  get hasCollectionFields(): boolean {
    const group = this.fieldGroups.find(g => g.entity === 'collections');
    return !!group && group.fields.some(f => f.selected);
  }

  get periodAvgWithoutRange(): boolean {
    return !this.collectionRange && this.selectedFields.some(f => f.key === PERIOD_AVG_KEY);
  }

  // ── Filtros generales ────────────────────────────────────────────────────
  onStatusFilter(options: any[]): void {
    // Copia: TabletStatusFilter muta en sitio su Input
    this.selectedStatusOptions = [...(options || [])];
    this.onGeneralFiltersChanged();
  }

  onCollectionDateFilter(event: { type: string; startDate: Date | null; endDate: Date | null }): void {
    if (!event || event.type === 'none' || !event.startDate || !event.endDate) {
      this.collectionRange = null;
      this.dateFilterValue = null;
    } else {
      this.collectionRange = { from: toCalendarDay(event.startDate), to: toCalendarDay(event.endDate) };
      // Se restaura como fechas concretas (reproducible), no como preset relativo
      this.dateFilterValue = { type: event.type, startDate: event.startDate, endDate: event.endDate };
    }
    this.onGeneralFiltersChanged();
  }

  /** Si ya hay un reporte aplicado, se re-ejecuta desde la página 1 para no mostrar datos desactualizados. */
  private onGeneralFiltersChanged(): void {
    if (this.lastAppliedKey !== null) this.runReport(1);
  }

  private buildFilters(): ReportFilters {
    const filters: ReportFilters = {};
    for (const key of LEGACY_FILTER_KEYS) {
      if (this.filters[key] !== undefined && this.filters[key] !== null && this.filters[key] !== '') {
        filters[key] = this.filters[key];
      }
    }
    const status = this.selectedStatusOptions.filter(o => o.field === 'status').map(o => o.value);
    const active = this.selectedStatusOptions.filter(o => o.field === 'active').map(o => o.value);
    if (status.length) filters.status = status;
    if (active.length) filters.active = active;
    // Sin campos de Collection Data el rango se ignora (no se convierte en filtro de alta del cliente)
    if (this.collectionRange && this.hasCollectionFields) {
      filters.collection_from = this.collectionRange.from;
      filters.collection_to = this.collectionRange.to;
    }
    return filters;
  }

  /** Único constructor del payload de consulta/exportación. */
  private buildBasePayload(): ReportBasePayload {
    const selectedKeys = this.selectedFields.map(f => f.key);
    const selectedBindings = new Set(selectedKeys.map(k => k.replace(/\./g, '_')));
    const sortBy = this.currentSort && selectedKeys.includes(this.currentSort.field) ? this.currentSort : null;
    return {
      selectedColumns: selectedKeys,
      filters: this.buildFilters(),
      // Filtros de columnas ya no seleccionadas no deben quedar como restricciones invisibles
      columnFilters: this.columnFilters.filter(cf => selectedBindings.has(cf.column)),
      ...(sortBy ? { sortBy } : {})
    };
  }

  private resetGridFilters(): void {
    this.columnFilters = [];
    this.currentSort = null;
    // El grid se destruye al quedar sin resultados ni filtros (showGrid = false), junto con su
    // modelo de filtros. No se llama setFilterModel: dispararía onFilterChanged → runReport.
    this.gridApi = null;
  }

  clearColumnFilters(): void {
    if (this.gridApi) {
      this.gridApi.setFilterModel(null); // dispara onFilterChanged → runReport(1)
    } else {
      this.columnFilters = [];
      this.runReport(1);
    }
  }

  /** El grid se mantiene visible con filtros de columna activos aunque no haya filas. */
  get showGrid(): boolean {
    return (this.hasResults || this.columnFilters.length > 0) && !this.loading;
  }

  selectAll(group: FieldGroup): void {
    const toggleableFields = group.fields.filter(f => !f.locked);
    const allSelected = toggleableFields.every(f => f.selected);
    toggleableFields.forEach(f => f.selected = !allSelected);
  }

  clearAll(): void {
    this.fieldGroups.forEach(g =>
      g.fields.forEach(f => { if (!f.locked) f.selected = false; })
    );
    this.activeQuery?.unsubscribe();
    this.activeQuery = null;
    this.loading = false;
    this.paging = false;
    this.resetGridFilters();
    this.filters = {};
    this.selectedStatusOptions = [];
    this.collectionRange = null;
    this.dateFilterValue = null;
    this.lastAppliedKey = null;
    this.gridData = [];
    this.gridColumns = [];
    this.columnDefs = [];
    this.hasResults = false;
    this.totalRecords = 0;
    this.currentPage = 1;
  }

  // ── Ejecutar reporte ──────────────────────────────────────────────────────
  runReport(page = 1): void {
    if (!this.selectedCount) {
      this.showFeedback('Please select at least one column.', 'error');
      return;
    }

    const isFilterOrPageRefresh = this.showGrid && (this.columnFilters.length > 0 || page > 1);

    if (!isFilterOrPageRefresh) {
      this.loading = true;
    } else {
      this.paging = true;  // overlay sutil sobre el grid existente
    }

    this.currentPage = page;

    const base = this.buildBasePayload();
    const payload: ReportQueryPayload = { ...base, page: this.currentPage, limit: this.pageSize };

    // Cancelar la petición anterior: una respuesta vieja no debe pisar a la nueva
    this.activeQuery?.unsubscribe();
    this.activeQuery = this.customReportService.runQuery(payload).subscribe({
      next: res => {
        if (!res.ok) { this.loading = false; this.paging = false; return; }
        this.lastAppliedKey = JSON.stringify(base);

        this.gridColumns = res.columns;
        this.updateColumnDefs(res.columns);
        this.totalRecords = res.total;
        this.gridData = res.data;

        this.hasResults = res.data.length > 0;
        this.loading = false;
        this.paging = false;

        if (!this.hasResults) {
          this.showFeedback('No records found for the selected criteria.', 'info');
        } else if (this.periodAvgWithoutRange) {
          this.showFeedback('Avg Gallons/Month (Period) requires a collection date range; it stays empty until you select one.', 'info');
        } else {
          this.feedbackMsg = '';
        }
      },
      error: (err) => {
        this.loading = false;
        this.paging = false;
        const msg = err?.status === 400 && err?.error?.msg ? err.error.msg : 'Error running report. Please try again.';
        this.showFeedback(msg, 'error');
      }
    });
  }

  // ── Paginación ────────────────────────────────────────────────────────────
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.runReport(page);
  }

  // ── Exportar Excel ────────────────────────────────────────────────────────
  exportExcel(): void {
    if (!this.selectedCount) {
      this.showFeedback('Please select at least one column before exporting.', 'error');
      return;
    }
    // Exportar exactamente lo que se ve: si hay cambios sin aplicar, pedir ejecutar el reporte antes
    const base = this.buildBasePayload();
    if (this.lastAppliedKey !== JSON.stringify(base) || this.loading || this.paging) {
      this.showFeedback('Run the report to apply your latest changes before exporting.', 'info');
      return;
    }
    this.showFeedback('Generating Excel file...', 'info');
    this.customReportService.exportExcel(base).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Nombre único con hora local hasta segundos: kite-custom-report-YYYY-MM-DD-HHmmss.xlsx
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const stamp = `${toCalendarDay(now)}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        a.download = `kite-custom-report-${stamp}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.showFeedback('Excel file downloaded successfully.', 'success');
      },
      error: async (err) => {
        // Errores 400/413 llegan como Blob: mostrar el mensaje del backend (p. ej. límite de filas)
        let msg = 'Error generating Excel file. Please try again.';
        if ((err?.status === 400 || err?.status === 413) && err?.error instanceof Blob) {
          try { msg = JSON.parse(await err.error.text())?.msg || msg; } catch { /* mensaje genérico */ }
        }
        this.showFeedback(msg, 'error');
      }
    });
  }

  // ── Vistas guardadas ──────────────────────────────────────────────────────
  loadSavedConfigs(): void {
    this.customReportService.getConfigs().subscribe({
      next: res => { if (res.ok) this.savedConfigs = res.configs; }
    });
  }

  loadConfig(config: ReportConfig): void {
    this.clearAll();

    config.selectedColumns.forEach(colKey => {
      this.fieldGroups.forEach(g => {
        const field = g.fields.find(f => f.key === colKey);
        if (field) {
          field.selected = true;
          g.expanded = true;
        }
      });
    });

    this.restoreFilters(config.filters || {});
    // Solo se restaura un orden que pertenezca a una columna seleccionada
    this.currentSort = config.sortBy?.field && this.selectedFields.some(f => f.key === config.sortBy!.field)
      ? { field: config.sortBy.field, direction: config.sortBy.direction }
      : null;
    this.showSavedPanel = false;
    this.showFeedback(`Loaded config: "${config.name}"`, 'success');
  }

  /** Restaura filtros de una vista (propia o compartida); descarta claves y valores no reconocidos. */
  private restoreFilters(saved: ReportFilters): void {
    this.filters = {};
    for (const key of LEGACY_FILTER_KEYS) {
      if (typeof saved[key] === 'string' && saved[key]) this.filters[key] = saved[key];
    }
    const asList = (v: any): string[] => Array.isArray(v) ? v : (typeof v === 'string' && v ? [v] : []);
    const wanted = [
      ...asList(saved.status).map(v => ({ field: 'status', value: v })),
      ...asList(saved.active).map(v => ({ field: 'active', value: v }))
    ];
    this.selectedStatusOptions = this.statusFilterOptions
      .filter(o => wanted.some(w => w.field === o.field && w.value === o.value));

    const start = fromCalendarDay(saved.collection_from as string);
    const end = fromCalendarDay(saved.collection_to as string);
    if (start && end) {
      this.collectionRange = { from: saved.collection_from as string, to: saved.collection_to as string };
      this.dateFilterValue = { type: 'custom', startDate: start, endDate: end };
    } else {
      this.collectionRange = null;
      this.dateFilterValue = null;
    }
  }

  openSaveModal(): void {
    if (!this.selectedCount) {
      this.showFeedback('Please select at least one column first.', 'error');
      return;
    }
    this.saveConfigName = '';
    this.saveConfigShared = false;
    this.saveModalOpen = true;
  }

  saveConfig(): void {
    if (!this.saveConfigName.trim()) return;

    const base = this.buildBasePayload();
    this.customReportService.saveConfig({
      name: this.saveConfigName.trim(),
      isShared: this.saveConfigShared,
      baseEntity: 'clients',
      selectedColumns: base.selectedColumns,
      filters: base.filters,
      ...(base.sortBy ? { sortBy: base.sortBy } : {})
    }).subscribe({
      next: res => {
        if (res.ok) {
          this.savedConfigs.unshift(res.config);
          this.saveModalOpen = false;
          this.showFeedback(`Report "${this.saveConfigName}" saved successfully.`, 'success');
        }
      },
      error: (err) => this.showFeedback(err?.status === 400 && err?.error?.msg ? err.error.msg : 'Error saving config.', 'error')
    });
  }

  deleteConfig(config: ReportConfig, event: Event): void {
    event.stopPropagation();
    if (!config._id) return;
    this.customReportService.deleteConfig(config._id).subscribe({
      next: res => {
        if (res.ok) {
          this.savedConfigs = this.savedConfigs.filter(c => c._id !== config._id);
          this.showFeedback('Config deleted.', 'success');
        }
      }
    });
  }

  // ── UI Helpers ────────────────────────────────────────────────────────────
  showFeedback(msg: string, type: 'success' | 'error' | 'info'): void {
    this.feedbackMsg = msg;
    this.feedbackType = type;
    if (type === 'success') {
      setTimeout(() => { this.feedbackMsg = ''; }, 3000);
    }
  }

  getSelectedCount(group: FieldGroup): number {
    return group.fields.filter(f => f.selected).length;
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const start = Math.max(1, this.currentPage - 2);
    const end = Math.min(total, this.currentPage + 2);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }
}
